import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Router } from '@techstyle/next-routes';
import {
  Collapsible,
  useCollapsibleState,
  useTheme,
  Video,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import generateCdnImageUrl from '../../../src/utils/generateCdnImageUrl';
import {
  trackModelCollapsibleAction,
  trackPdpModelThumbnailsClick,
  trackViewedPdpImage,
} from '../../actions/products';
import { prepModelData } from '../../segment-events/utils';
import { desktop, mobile } from '../../styles';
import fetchVideoThumbnail from '../../utils/fetchVideoThumbnail';
import formatProductUrl from '../../utils/formatProductUrl';
import formatVimeoThumbnailUrl from '../../utils/formatVimeoThumbnailUrl';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { Component as Carousel } from '../Carousel';
import NewThemeIcon from '../NewThemeIcon/NewThemeIcon';
import { Component as ProductImage } from '../ProductImage';
import { VideoCoverSizes } from '../ProductVideo/ProductVideo';

const MODEL_THUMBNAIL_CAROUSEL_OFFSET = 128;

const Wrapper = styled.div`
  position: relative;

  padding: ${props => props.theme.spacing.tiny}px;

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    margin-left: ${MODEL_THUMBNAIL_CAROUSEL_OFFSET}px;
  }

  ${desktop`
    margin-top: -${props => props.theme.spacing.small}px;
  `}
`;

const StyledCollapsible = styled(Collapsible)``;

const StyledCollapsibleButton = styled(Collapsible.Button)`
  ${props => props.theme.collapsibleTrigger.defaultStyle};
  line-height: 14px;
  text-transform: uppercase;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  border: none;
  padding: 0px ${props => props.theme.spacing.tiny}px;

  &:focus {
    outline: none;
    border: none;
  }

  align-items: center;
`;

const CollapsibleLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.spacing.tiny}px 0px;
`;

const CollapsibleContent = styled(Collapsible.Content)``;

const NewDownIcon = styled(NewThemeIcon).attrs({
  type: 'caretDown',
  size: 24,
})``;

const NewUpIcon = styled(NewThemeIcon).attrs({
  type: 'caretUp',
  size: 24,
})``;

const ModelNameSizeLabel = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
  align-items: center;
`;

const SeeMoreModelMessage = styled.div`
  margin-top: 4px;

  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.gunmetal};
`;

const StyledCarousel = styled(Carousel)`
  padding: ${props => props.theme.spacing.tiny}px 0px;
  @media (min-width: ${props => props.theme.breakpoints.xlarge - 1}px) {
    .slick-list {
      padding-bottom: 2px;
      margin: 0px ${props => props.theme.spacing.medium}px;
    }
    .slick-arrow {
      top: 0px;
      width: 25px;
      height: 25px;
      font-size: 4px;
      text-align: center;
      border-radius: 50%;
      margin-top: ${props => props.theme.spacing.large}px;
      border: solid 1px ${props => props.theme.colors.lavender250};
      color: ${props => props.theme.colors.active};
      background-color: ${props => props.theme.colors.white};
    }
    .slick-disabled {
      display: none !important;
      pointer-events: none;
    }
    svg {
      width: 10px;
      height: 12px;
    }
  }
`;

const SingleThumbnailContainer = styled.div`
  cursor: pointer;
  text-align: center;
  padding: 0px ${props => props.theme.spacing.tiny}px;

  &:focus {
    outline: none;
  }
`;

const SingleThumbnail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModelImageContainer = styled.div`
  width: 80px;
  height: 80px;
  padding: 2px;
  position: relative;

  ${mobile`
    width: 78px;
    height: 78px;
  `}

  &:before {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    content: '';
    z-index: -1;
    position: absolute;
    border-radius: 50%;
    background: ${props => props.theme.colors.default};

    ${props =>
      props.isSelected &&
      `
      opacity:1;
      transition: 800ms linear opacity;
    `}
  }

  &:hover {
    ${props =>
      !props.isSelected &&
      `
      opacity: 0.7;
      border-radius: 50%;
      background-color: ${props.theme.colors.active}
    `}
  }
`;

// crop rectangle model image to circle
const ModelImageCropper = styled.div`
  z-index: 1;
  width: 76px;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  position: relative;
  border: 2px solid ${props => props.theme.colors.white};

  ${mobile`
    width: 74px;
    height: 74px;
  `}
`;

const ModelSizeLabel = styled.div`
  margin-top: 4px;
`;

function ToggleButton({
  selectedModel,
  shouldShowMoreModelsMsg,
  isAmbassadorModelSelected,
}) {
  const { isOpen } = useCollapsibleState();

  const renderModelInfoLabel = useCallback(() => {
    const { description } = selectedModel;
    if (selectedModel.isCombinedModel) {
      const labels = description
        .split('|')
        .map(description => description.trim());
      return isAmbassadorModelSelected ? labels[0] : labels[1];
    }
    return description;
  }, [isAmbassadorModelSelected, selectedModel]);

  return (
    <StyledCollapsibleButton>
      <CollapsibleLabel>
        <ModelNameSizeLabel>
          <NewThemeIcon type="person" size={24} />
          {renderModelInfoLabel()}
        </ModelNameSizeLabel>
        {shouldShowMoreModelsMsg && (
          <SeeMoreModelMessage>
            <FormattedMessage
              id="site_pdp.see_other_models"
              defaultMessage="See other models in different sizes"
            />
          </SeeMoreModelMessage>
        )}
      </CollapsibleLabel>
      {isOpen ? <NewUpIcon /> : <NewDownIcon />}
    </StyledCollapsibleButton>
  );
}

ToggleButton.propTypes = {
  selectedModel: PropTypes.object,
  shouldShowMoreModelsMsg: PropTypes.bool,
  isAmbassadorModelSelected: PropTypes.bool,
};

export default function ProductDetailModelThumbnails({
  isAmbassadorModelSelected,
  isCurvy = false,
  isProductSet = false,
  models = [],
  onAmbassadorModelSelect,
  onChangeModel = () => {},
  selectedModel,
  trackViewedPdpImageWithSessionStorage = () => {},
}) {
  const router = useRouter();
  const { query } = router;
  const theme = useTheme();
  const dispatch = useDispatch();
  const carouselRef = useRef();

  // `react-collapsible` triggers `onOpen` on first page load & whenever a
  // new model is selected. This lets us ignore `onOpen` in those scenarios.
  const [shouldIgnoreOnOpen, setShouldIgnoreOnOpen] = useState(false);
  const [selectedModelIndex, setSelectedModelIndex] = useState(null);

  const { 'match-user-size-with-model-size': isMatchUserSizeFeatureEnabled } =
    useLDFlags();

  const trackViewedPdpImages = useCallback(
    async model => {
      let imageUrl;
      const angle = model.angles[0];

      const { vimeo_id: vimeoId } = model.product;

      if (angle === 'video' && vimeoId) {
        try {
          const videoUrl = Video.getUrlFromVideoId(vimeoId);

          const data = await fetchVideoThumbnail(videoUrl);

          if (data.thumbnail_url) {
            imageUrl = formatVimeoThumbnailUrl(
              data.thumbnail_url,
              VideoCoverSizes.SMALL
            );
          }
        } catch (err) {
          Sentry.captureException(err);
        }
      } else {
        imageUrl = generateCdnImageUrl({
          product: model.product,
          modelIndex: model.modelIndex,
          angle,
        });
      }

      const {
        master_product_id: _masterProductId = -1,
        product_id_object_list: productIdObjectList,
      } = model.product;

      const masterProductId = _masterProductId.toString();

      trackViewedPdpImageWithSessionStorage(imageUrl, () => {
        const modelDataInfo = prepModelData(model);
        const productDetail = productIdObjectList?.find(
          item => item.size === modelDataInfo.model_size
        );
        dispatch(
          trackViewedPdpImage({
            position: 1,
            location: 'model selection',
            bundle_product_id_hit: isProductSet ? masterProductId : null,
            label: 'click',
            image_url: imageUrl,
            is_plus_size: productDetail?.is_plus_size || isCurvy,
            product_id_hit: masterProductId,
            ...modelDataInfo,
          })
        );
      });
    },
    [dispatch, isCurvy, isProductSet, trackViewedPdpImageWithSessionStorage]
  );

  const getModelIndex = useCallback(
    model => models.findIndex(_model => _model.modelId === model?.modelId),
    [models]
  );

  const handleModelChange = useCallback(
    async model => {
      const { psrc } = query;
      const { product, isLinkedProduct } = model;
      if (isLinkedProduct) {
        const { as, href } = formatProductUrl({
          extraQueryParams: {
            psrc,
            isClickFromPdp: true,
          },
          permalink: product.permalink,
          productId: product.master_product_id,
        });
        Router.replace(href, as);
      }
      if (
        model.modelId !== selectedModel.modelId ||
        model.isAmbassadorModel !== isAmbassadorModelSelected
      ) {
        setShouldIgnoreOnOpen(true);
      }
      onChangeModel(model.modelId);
      await trackViewedPdpImages(model);
      onAmbassadorModelSelect(model.isAmbassadorModel);
      dispatch(trackPdpModelThumbnailsClick(model));
      setSelectedModelIndex(getModelIndex(model));
    },
    [
      dispatch,
      getModelIndex,
      isAmbassadorModelSelected,
      onAmbassadorModelSelect,
      onChangeModel,
      query,
      selectedModel.modelId,
      trackViewedPdpImages,
    ]
  );

  const trackCollapsibleAction = eventLabel => {
    dispatch(trackModelCollapsibleAction({ eventLabel, model: selectedModel }));
  };

  const carouselOptions = {
    arrows: true,
    dots: false,
    slidesToShow: 4,
    slidesToScroll: 4,
    responsive: [
      {
        breakpoint: theme.breakpoints.xlarge,
        settings: {
          arrows: false,
          slidesToShow: 4,
          slidesToScroll: 1,
          swipeToSlide: true,
        },
      },
    ],
  };

  const hasAvailableModels = models.length > 0;
  const shouldShowMoreModelsMsg = models.length > 1;
  const shouldDefaultToOpen = models.length > 1;

  /*
   * Anytime the product model changes, we want the model to appear on the screen
   * so that customers do not need to swipe/scroll anymore.
   */
  useEffect(() => {
    const selectedModelPropsIndex = getModelIndex(selectedModel);

    if (
      isMatchUserSizeFeatureEnabled &&
      selectedModelPropsIndex !== selectedModelIndex &&
      carouselRef?.current
    ) {
      setSelectedModelIndex(selectedModelPropsIndex);
      carouselRef.current.slickGoTo(selectedModelPropsIndex, true);
    }
  }, [
    getModelIndex,
    isMatchUserSizeFeatureEnabled,
    selectedModel,
    selectedModelIndex,
  ]);

  return hasAvailableModels ? (
    <Wrapper>
      <StyledCollapsible
        defaultOpen={shouldDefaultToOpen}
        onOpen={() => {
          if (shouldIgnoreOnOpen) {
            return setShouldIgnoreOnOpen(false);
          }
          trackCollapsibleAction('Drawer Opened');
        }}
        onClose={() => trackCollapsibleAction('Drawer Closed')}
      >
        <ToggleButton
          shouldShowMoreModelsMsg={shouldShowMoreModelsMsg}
          selectedModel={selectedModel}
          isAmbassadorModelSelected={isAmbassadorModelSelected}
        />
        <CollapsibleContent>
          <StyledCarousel options={carouselOptions} sliderRef={carouselRef}>
            {models.map(model => {
              const isSelected =
                model.modelId === selectedModel.modelId &&
                (isAmbassadorModelSelected
                  ? model.isAmbassadorModel
                  : !model.isAmbassadorModel);
              return (
                <SingleThumbnailContainer
                  key={model.modelIndex}
                  onClick={() => handleModelChange(model)}
                >
                  <SingleThumbnail>
                    <ModelImageContainer isSelected={isSelected}>
                      <ModelImageCropper>
                        <ProductImage
                          product={model.product}
                          modelIndex={model.modelIndex}
                          angle={model.thumbnailAngleIndex}
                          size={6}
                          alt="Model Thumbnail Image"
                          lazyLoad
                        />
                      </ModelImageCropper>
                    </ModelImageContainer>
                    <ModelSizeLabel
                      isSelected={isSelected}
                      data-testid="model-size"
                    >
                      {model.modelSize}
                    </ModelSizeLabel>
                  </SingleThumbnail>
                </SingleThumbnailContainer>
              );
            })}
          </StyledCarousel>
        </CollapsibleContent>
      </StyledCollapsible>
    </Wrapper>
  ) : null;
}

ProductDetailModelThumbnails.propTypes = {
  isAmbassadorModelSelected: PropTypes.bool,
  isCurvy: PropTypes.bool,
  isProductSet: PropTypes.bool,
  models: PropTypes.array,
  onAmbassadorModelSelect: PropTypes.func,
  onChangeModel: PropTypes.func,
  selectedModel: PropTypes.object,
  trackViewedPdpImageWithSessionStorage: PropTypes.func,
};
